/* eslint-disable react/destructuring-assignment */
import '../index.css'
import '@elevate_security/elevate-component-library/dist/assets/css/index.css'
import 'react-datepicker/dist/react-datepicker.css'

import React, { lazy, Suspense, useEffect } from 'react'
import PropTypes from 'prop-types'
import ReactDOM from 'react-dom'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import * as FullStory from '@fullstory/browser'

import Loading from './EngagementPlatform/scenes/Session/components/Loading/Loading'
import { connected } from './services/actions'

// components
import NotFound from './NotFound'
import {
  getDarklyFlags,
  getPendoValue
} from './EngagementPlatform/scenes/Session/services/selectors/darklySelectors'
import darkly from './EngagementPlatform/services/darkly'
import environment from '../environment'
import {
  getOfferingPlan,
  getOrganizationsCompany
} from './EngagementPlatform/scenes/Session/services/selectors/organizationsSelectors'
import { getAdminMe } from './EngagementPlatform/scenes/Session/services/selectors/adminSelectors'

const AsyncEngagementPlatform = lazy(() => import('./EngagementPlatform'))
const AsyncHackerMind = lazy(() => import('./HackerMind'))
const AsyncGroupScheduling = lazy(() => import('./GroupScheduling'))
const AsyncManagerDashboard = lazy(() => import('./ManagerDashboard'))

const Elevate = require('@elevate_security/elevate-component-library')

const getConfirmation = (message, callback) => {
  const display = 'block'
  const holder = document.getElementById('alert')

  const userAction = (bool) => {
    ReactDOM.unmountComponentAtNode(holder)
    callback(bool)
  }

  ReactDOM.render(
    <div className="alert alert-warning d-flex justify-content-between" style={{ display }}>
      <div>
        <strong>Unsaved Changes</strong>
        &nbsp;
        <span>{message}</span>
      </div>
      <div>
        <button
          className="confirm float-right btn btn-sm btn-primary"
          type="button"
          aria-label="Confirm"
          onClick={() => userAction(true)}
        >
          <span aria-hidden="true">OK</span>
        </button>
        <button
          className="cancel float-right btn btn-sm btn-outline-success-custom mr-1"
          type="button"
          aria-label="Cancel"
          onClick={() => userAction(false)}
        >
          <span aria-hidden="true">Cancel</span>
        </button>
      </div>
    </div>,
    holder
  )
}

function ElevateSecurity() {
  const dispatch = useDispatch()
  const darklyFlags = useSelector(getDarklyFlags)
  const { isLDReady, initFullStory } = darklyFlags

  const pendoValue = useSelector(getPendoValue)
  const offeringPlan = useSelector(getOfferingPlan)
  const company = useSelector(getOrganizationsCompany)
  const adminMe = useSelector(getAdminMe)

  const getPendo = () => {
    if (!pendoValue || !Object.keys(adminMe || {}).length || !Object.keys(company || {}).length)
      return
    // eslint-disable-next-line  no-undef
    pendo.initialize({
      visitor: {
        id: adminMe?.email,
        email: adminMe?.email,
        full_name: adminMe?.full_name,
        role: adminMe?.es_role
      },

      account: {
        id: company?.salesforce_id || `org-${company?.id}`,
        name: company?.name,
        is_paying: company?.organization_type === 'STD',
        planLevel: offeringPlan,
        creationDate: company?.created
      }
    })
  }

  useEffect(() => {
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)

    initializeDarklyAnonymously()

    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isLDReady && initFullStory) {
      initializeFullStory()
    }
  }, [isLDReady, initFullStory])

  useEffect(() => {
    getPendo()
  }, [pendoValue, offeringPlan, company, adminMe])
  const initializeDarklyAnonymously = () => {
    const [subdomain] = window.location.host.split('.')
    const [, tenant] = subdomain.match(/^(.*?)(-(pr-[0-9]+))?$/)

    // initialize the client with an anonymous user
    const user = {
      key: 'anonymous',
      custom: {
        organization: tenant
      }
    }
    darkly.identify(user)
  }

  const initializeFullStory = () => {
    FullStory.init({ orgId: environment['FULL_STORY_ORG_ID'] })
  }

  const online = () => {
    dispatch(connected(true))
  }

  const offline = () => {
    dispatch(connected(false))
  }

  return (
    <Suspense fallback={<Loading />}>
      <Router getUserConfirmation={getConfirmation}>
        <Switch>
          <Route path="/engagement" render={(props) => <AsyncEngagementPlatform {...props} />} />
          <Route
            path="/:campaignId/scheduled"
            render={(props) => <AsyncGroupScheduling {...props} />}
          />
          <Route path="/hackersmind/:session" render={(props) => <AsyncHackerMind {...props} />} />
          <Route
            path="/vision2/:profileId/profile"
            render={(props) => <AsyncManagerDashboard {...props} />}
          />
          <Route
            path="/vision2/manager-dashboard/team-trends/:person_nid?"
            render={(props) => <AsyncManagerDashboard {...props} />}
          />
          <Route
            path="/vision2/manager-dashboard/:person_nid?"
            render={(props) => <AsyncManagerDashboard {...props} />}
          />
          <Redirect from="/" to="/engagement" />
          <Route path="*" component={NotFound} />
        </Switch>
      </Router>
      <Elevate.GlobalStyle />
    </Suspense>
  )
}

ElevateSecurity.propTypes = {
  connected: PropTypes.func.isRequired
}

export default ElevateSecurity
